import { LiliTextInput } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { ControllerWrapper } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index.ts';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@Services/utils/utils.ts';
import { Line1AutocompleteInput } from '@Components/inputs/Line1Autocomplete/Line1AutocompleteInput';
import { AddressStateInput } from '@Components/inputs/AddressStateDropDown';
import { ZipInput } from '@Components/inputs/ZipAddress';
import type { LiliDropdownItem } from '@lili/lili-frontend-common';
import type { Country } from '@Types/country';

export interface IMailingAddressForm {
  [OnboardingFormFields.LINE1]: string;
  [OnboardingFormFields.APARTMENT]: string;
  [OnboardingFormFields.CITY]: string;
  [OnboardingFormFields.STATE]: LiliDropdownItem | null;
  [OnboardingFormFields.ZIP]: string;
  [OnboardingFormFields.COUNTRY]?: Country | null;
}

export interface IMailingAddressFormProps {
  disabled: boolean;
  usStates?: { title: string; value: string }[];
}

export const MailingAddressForm = ({ disabled, usStates }: IMailingAddressFormProps) => {
  const { t } = useTranslation();
  const baseTabIndex = getRouteOrdinal(RoutesMap.HOME_ADDRESS);

  return (
    <>
      <Line1AutocompleteInput disabled={disabled} tabIndex={baseTabIndex} validStates={usStates} />

      <div className='flex flex-col items-start w-full'>
        <div className='w-full'>
          <ControllerWrapper name={OnboardingFormFields.APARTMENT}>
            {({ value, onBlur, onChange, error }) => (
              <LiliTextInput
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                id={OnboardingFormFields.APARTMENT}
                title={t('pages.mailingAddress.lineTwoFieldTitle')}
                errorMessage={error?.message}
                tabIndex={baseTabIndex + 1}
                disabled={disabled}
                isPublic
              />
            )}
          </ControllerWrapper>
        </div>

        <div className='w-full'>
          <ControllerWrapper name={OnboardingFormFields.CITY}>
            {({ value, onBlur, onChange, error }) => (
              <LiliTextInput
                value={value}
                onBlur={() => {
                  onChange(normalizeInputSpacesOnBlur(value));
                  onBlur();
                }}
                onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
                id={OnboardingFormFields.CITY}
                title={t('pages.mailingAddress.cityFieldTitle')}
                errorMessage={error?.message}
                disabled={disabled}
                tabIndex={baseTabIndex + 2}
                isPublic
              />
            )}
          </ControllerWrapper>
        </div>
      </div>

      <div className='w-full flex sm:flex-row flex-col sm:gap-[16px]'>
        <div className='flex-1' data-testid='state_dropdown_wrapper'>
          <AddressStateInput
            disabled={disabled}
            tabIndex={baseTabIndex + 3}
            statesList={usStates}
          />
        </div>

        <div className='flex-1'>
          <ZipInput isUS={true} disabled={disabled} tabIndex={baseTabIndex + 5} />
        </div>
      </div>
    </>
  );
};
